import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { checkRole, checkPermission } from "./roleUtils";

/**
 * AuthProvider — drop-in replacement for techveda-react-auth's AuthProvider.
 *
 * Props:
 *   children
 *   storageKey               — localStorage key for the serialized user (default "auth_user")
 *   onLogin(user)            — callback fired after a successful login()
 *   onLogout()               — async/sync callback fired before logout completes
 *   enableRouterIntegration  — when true, logout() navigates to "/"
 */
export const AuthProvider = ({
  children,
  storageKey = "auth_user",
  onLogin,
  onLogout,
  enableRouterIntegration = false,
}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // useNavigate is always available because we import react-router-dom at the top.
  // We only invoke it from logout() when enableRouterIntegration is true.
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch (err) {
      // Same swallow behavior as the upstream lib — corrupted storage shouldn't crash boot.
      // eslint-disable-next-line no-console
      console.error("Failed to parse user data", err);
    } finally {
      setLoading(false);
    }
  }, [storageKey]);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem(storageKey, JSON.stringify(userData));
    onLogin?.(userData);
  };

  const logout = async () => {
    setUser(null);
    localStorage.removeItem(storageKey);
    await onLogout?.();
    if (enableRouterIntegration) {
      navigate("/");
    }
  };

  const hasRole = (role) => checkRole(user, role);
  const hasPermission = (permission) => checkPermission(user, permission);

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
    hasRole,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
